/**
 * LLM 智能拆分镜配置（管理后台）
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../../Toast';
import {
  directorAdminApi,
  type ShotSplitConfig,
} from '../../../apps/director/services/directorAdminApi';
import LlmModelPicker from '../llm/LlmModelPicker';

const DEFAULT_FORM: ShotSplitConfig = {
  shotSplitLlmEnabled: false,
  shotSplitLlmModelId: '',
  shotSplitLlmSystemPrompt: '',
  shotSplitCreditsPerCall: 5,
  shotSplitRateLimitPerMinute: 3,
};

export default function DirectorShotSplitConfigTab() {
  const [form, setForm] = useState<ShotSplitConfig>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    directorAdminApi
      .getShotSplitConfig()
      .then((cfg) => setForm(cfg))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof ShotSplitConfig>(key: K, value: ShotSplitConfig[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // 校验 modelId 必须是合法 uuid（避免老字符串误写回）
    const modelId = (form.shotSplitLlmModelId ?? '').trim();
    if (form.shotSplitLlmEnabled && modelId && !isUuid(modelId)) {
      toast.error('请重新选择 LLM 模型（当前值非合法模型 ID）');
      return;
    }
    setSaving(true);
    try {
      const patch: Partial<ShotSplitConfig> = {
        shotSplitLlmEnabled: form.shotSplitLlmEnabled,
        shotSplitLlmModelId: modelId || null,
        shotSplitLlmSystemPrompt: form.shotSplitLlmSystemPrompt,
        shotSplitCreditsPerCall: form.shotSplitCreditsPerCall,
        shotSplitRateLimitPerMinute: form.shotSplitRateLimitPerMinute,
      };

      await directorAdminApi.updateShotSplitConfig(patch);
      toast.success('已保存');
      const fresh = await directorAdminApi.getShotSplitConfig();
      setForm(fresh);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  function isUuid(v: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="card p-6 max-w-3xl space-y-5">
      <div>
        <h3 className="text-lg font-semibold mb-1">LLM 智能拆分镜</h3>
        <p className="text-sm text-light-muted">
          配置 LLM provider 让用户能用整段剧本一键拆出分镜。
        </p>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.shotSplitLlmEnabled}
          onChange={(e) => update('shotSplitLlmEnabled', e.target.checked)}
        />
        <span className="text-sm font-medium">启用智能拆分镜</span>
      </label>

      <div>
        <label className="block text-sm font-medium mb-1">LLM 模型</label>
        <LlmModelPicker
          value={form.shotSplitLlmModelId}
          onChange={(id) => update('shotSplitLlmModelId', id)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">System Prompt（可选）</label>
        <textarea
          value={form.shotSplitLlmSystemPrompt ?? ''}
          onChange={(e) => update('shotSplitLlmSystemPrompt', e.target.value)}
          rows={6}
          placeholder="留空则使用内置的默认拆分镜 prompt"
          className="w-full input text-sm font-mono"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">单次扣费积分</label>
          <input
            type="number"
            min={0}
            value={form.shotSplitCreditsPerCall}
            onChange={(e) => update('shotSplitCreditsPerCall', parseInt(e.target.value, 10) || 0)}
            className="w-full input text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">每用户每分钟最多调用次数</label>
          <input
            type="number"
            min={1}
            value={form.shotSplitRateLimitPerMinute}
            onChange={(e) => update('shotSplitRateLimitPerMinute', parseInt(e.target.value, 10) || 1)}
            className="w-full input text-sm"
          />
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-5 py-2 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
